import React, { useContext, useState, useEffect } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Container,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Stack,
  alpha,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Skeleton,
} from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import { ColorModeContext } from "../../contexts/ColorModeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useUI } from "../../contexts/UIContext";
import {
  FONT_MONO,
  HEADER_HEIGHT,
  MASTHEAD_BG,
  MASTHEAD_FG,
  MASTHEAD_MUTED,
  MASTHEAD_RULE,
  MASTHEAD_BG_LIGHT,
  MASTHEAD_FG_LIGHT,
  MASTHEAD_MUTED_LIGHT,
  MASTHEAD_RULE_LIGHT,
} from "../../theme";
import logo from "../../assets/logos/silver_gradient.webp";

const preloadMap: Record<string, () => void> = {
  "/": () => {
    import("../../pages/Home");
  },
  "/signal": () => {
    import("../../pages/Signal");
  },
  "/agent-kits": () => {
    import("../../pages/AgentKits");
  },
  "/blog": () => {
    import("../../pages/Blog");
  },
  "/resources": () => {
    import("../../pages/Resources");
  },
  "/about": () => {
    import("../../pages/About");
  },
  "/contact": () => {
    import("../../pages/Contact");
  },
};

/**
 * Masthead ember. The masthead is constant-dark in both modes, so this is the
 * dark-mode secondary.main hardcoded — same convention as HeroBand's kicker.
 */
const MASTHEAD_EMBER = "#FF7849";

/**
 * Mono "AI" chip beside the wordmark — the one place the brand mark wears
 * ember chrome. Spacing from the wordmark comes from the lockup's flex gap.
 */
const aiChipSx = {
  display: "inline-flex",
  alignItems: "center",
  px: 0.75,
  py: 0.25,
  fontFamily: FONT_MONO,
  fontSize: "0.6875rem", // 11px
  fontWeight: 500,
  lineHeight: 1,
  color: MASTHEAD_EMBER,
  border: "1px solid rgba(255,120,73,0.4)",
  borderRadius: "4px",
} as const;

/** Outlined ghost button on the obsidian masthead (Contact Us). */
const ctaButtonSx = {
  color: MASTHEAD_FG,
  borderColor: MASTHEAD_RULE,
  borderRadius: "6px",
  fontWeight: 500,
  whiteSpace: "nowrap",
  flexShrink: 0,
  "&:hover": {
    borderColor: "rgba(255,255,255,0.32)",
    bgcolor: "transparent",
  },
} as const;

/** Shared styling for the obsidian mobile-drawer list items (44px targets). */
const drawerItemSx = {
  borderRadius: 1,
  minHeight: 44,
  px: 2,
  color: MASTHEAD_MUTED,
  transition:
    "background-color 150ms cubic-bezier(0.23, 1, 0.32, 1), color 150ms cubic-bezier(0.23, 1, 0.32, 1)",
  "&:hover": { bgcolor: alpha(MASTHEAD_FG, 0.06), color: MASTHEAD_FG },
  "&.Mui-selected": {
    bgcolor: MASTHEAD_RULE,
    color: MASTHEAD_FG,
    "&:hover": { bgcolor: MASTHEAD_RULE },
  },
} as const;

export const Header: React.FC = () => {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const { user, profile, loading, signOut, isAdmin } = useAuth();
  const { openLoginModal } = useUI();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Masthead inverts with the theme: obsidian in dark, paper in light. The
  // drawer stays obsidian in both modes (a dark mobile menu is intentional).
  const isDark = theme.palette.mode === "dark";
  const bar = {
    bg: isDark ? MASTHEAD_BG : MASTHEAD_BG_LIGHT,
    fg: isDark ? MASTHEAD_FG : MASTHEAD_FG_LIGHT,
    muted: isDark ? MASTHEAD_MUTED : MASTHEAD_MUTED_LIGHT,
    rule: isDark ? MASTHEAD_RULE : MASTHEAD_RULE_LIGHT,
  };
  // Ember accent tuned per ground: bright on obsidian, deeper on paper.
  const barEmber = isDark ? "#FF7849" : "#B33A0F";
  const navHover = isDark ? "rgba(255,255,255,0.07)" : "rgba(11,11,12,0.05)";

  // Scroll-elevation: the bar gains blur + a hairline (and, light only, a soft
  // shadow) once content scrolls beneath it, so it separates from the page.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const allNavItems = [
    { label: "Signal Desk", href: "/", adminOnly: false },
    { label: "Agent Kits", href: "/agent-kits", adminOnly: false },
    { label: "Blog", href: "/blog", adminOnly: false },
    { label: "Resources", href: "/resources", adminOnly: false },
    { label: "About", href: "/about", adminOnly: false },
    { label: "Contact", href: "/contact", adminOnly: false },
    { label: "Training", href: "/training", adminOnly: true },
    { label: "Certifications", href: "/certifications", adminOnly: true },
    { label: "Enterprise", href: "/enterprise", adminOnly: true },
  ];

  const mainNavItems = allNavItems.filter((item) => !item.adminOnly || isAdmin);

  // Pathname-PREFIX matching so /blog/:slug lights "Blog". The Signal Desk
  // entry ("/") is active on exactly "/" or anything under /signal.
  const isActive = (href: string) =>
    href === "/"
      ? location.pathname === "/" || location.pathname.startsWith("/signal")
      : location.pathname === href || location.pathname.startsWith(`${href}/`);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await signOut();
    navigate("/");
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return "U";
    // If it looks like an email (contains @), take first 2 chars
    if (name.includes("@")) {
      return name.substring(0, 2).toUpperCase();
    }
    // Otherwise split by space and take first letter of each part
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const drawer = (
    <Box
      onClick={handleDrawerToggle}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: MASTHEAD_BG,
        color: MASTHEAD_FG,
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          borderBottom: `1px solid ${MASTHEAD_RULE}`,
        }}
      >
        <img
          src={logo}
          alt="Obsidian Ridge Logo"
          width={26}
          height={26}
          style={{ height: 26, width: 26 }}
        />
        <Typography
          component="span"
          sx={{ fontSize: "0.9375rem", fontWeight: 600, color: MASTHEAD_FG }}
        >
          Obsidian Ridge
        </Typography>
        <Box component="span" sx={aiChipSx}>
          AI
        </Box>
      </Box>
      <List sx={{ flexGrow: 1, pt: 1.5, px: 1.5 }}>
        {mainNavItems.map((item) => {
          const active = isActive(item.href);
          return (
            <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                component={RouterLink}
                to={item.href}
                selected={active}
                aria-current={active ? "page" : undefined}
                sx={drawerItemSx}
              >
                <ListItemText
                  primary={item.label}
                  slotProps={{
                    primary: {
                      fontSize: "0.9375rem",
                      fontWeight: active ? 600 : 500,
                      color: "inherit",
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
        <ListItem
          disablePadding
          sx={{
            mb: 0.5,
            mt: 2,
            borderTop: `1px solid ${MASTHEAD_RULE}`,
            pt: 2,
            display: "block", // Ensure items stack vertically
          }}
        >
          {user ? (
            <>
              <Box sx={{ display: "flex", alignItems: "center", px: 2, mb: 2 }}>
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    mr: 2,
                    bgcolor: "neutral.700",
                    color: MASTHEAD_FG,
                    fontSize: "1rem",
                  }}
                >
                  {getInitials(profile?.full_name || user.email)}
                </Avatar>
                <Box sx={{ overflow: "hidden" }}>
                  <Typography variant="subtitle2" noWrap sx={{ color: MASTHEAD_FG }}>
                    {profile?.full_name || "My Account"}
                  </Typography>
                  <Typography variant="caption" noWrap sx={{ color: MASTHEAD_MUTED }}>
                    {user.email}
                  </Typography>
                </Box>
              </Box>

              {/* Account Links */}
              <ListItemButton
                onClick={() => {
                  navigate("/account?tab=Overview");
                  setMobileOpen(false);
                }}
                sx={{ ...drawerItemSx, mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
                  <PersonOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Account" />
              </ListItemButton>

              <ListItemButton
                onClick={() => {
                  navigate("/account?tab=Settings");
                  setMobileOpen(false);
                }}
                sx={{ ...drawerItemSx, mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Settings" />
              </ListItemButton>

              <ListItemButton onClick={handleLogout} sx={drawerItemSx}>
                <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Sign Out" />
              </ListItemButton>
            </>
          ) : (
            <ListItemButton
              onClick={() => {
                setMobileOpen(false);
                openLoginModal();
              }}
              sx={drawerItemSx}
            >
              <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
                <PersonOutlineIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Sign In" />
            </ListItemButton>
          )}
        </ListItem>
      </List>
      <Box sx={{ p: 2.5, borderTop: `1px solid ${MASTHEAD_RULE}` }}>
        <Button
          fullWidth
          component={RouterLink}
          to="/contact"
          variant="outlined"
          size="small"
          sx={ctaButtonSx}
        >
          Contact Us
        </Button>
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={1}
          sx={{ mt: 1.5 }}
        >
          <Typography variant="caption" sx={{ color: MASTHEAD_MUTED }}>
            Toggle theme
          </Typography>
          <IconButton
            onClick={colorMode.toggleColorMode}
            size="small"
            aria-label="Toggle dark mode"
            sx={{ color: MASTHEAD_MUTED, "&:hover": { color: MASTHEAD_FG } }}
          >
            {theme.palette.mode === "dark" ? (
              <Brightness7Icon fontSize="small" />
            ) : (
              <Brightness4Icon fontSize="small" />
            )}
          </IconButton>
        </Stack>
      </Box>
    </Box>
  );

  const renderUserActions = () => {
    if (loading) {
      return (
        <Skeleton variant="circular" width={32} height={32} sx={{ bgcolor: alpha(bar.fg, 0.1) }} />
      );
    }

    if (user) {
      return (
        <>
          <IconButton
            onClick={handleMenuOpen}
            size="small"
            sx={{ ml: 1 }}
            aria-label="Account menu"
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: "0.875rem",
                bgcolor: "neutral.700",
                color: MASTHEAD_FG,
              }}
            >
              {getInitials(profile?.full_name || user.email)}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem onClick={() => navigate("/account?tab=Overview")}>
              <ListItemIcon>
                <PersonOutlineIcon fontSize="small" />
              </ListItemIcon>
              Account
            </MenuItem>
            <MenuItem onClick={() => navigate("/account?tab=Settings")}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </>
      );
    }

    return (
      <Button
        onClick={openLoginModal}
        variant="text"
        size="small"
        startIcon={<PersonOutlineIcon />}
        sx={{
          fontWeight: 500,
          color: bar.muted,
          "&:hover": {
            color: bar.fg,
            bgcolor: "transparent",
          },
        }}
      >
        Sign In
      </Button>
    );
  };

  return (
    <>
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: "absolute",
          left: "-9999px",
          top: "auto",
          width: "1px",
          height: "1px",
          overflow: "hidden",
          zIndex: 9999,
          "&:focus": {
            position: "fixed",
            top: 8,
            left: 8,
            width: "auto",
            height: "auto",
            px: 3,
            py: 1.5,
            bgcolor: "primary.main",
            color: "primary.contrastText",
            borderRadius: 1,
            fontSize: "0.875rem",
            fontWeight: 600,
            textDecoration: "none",
            boxShadow: 4,
          },
        }}
      >
        Skip to main content
      </Box>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          height: HEADER_HEIGHT,
          color: bar.fg,
          // Newspaper flag: 2px ember rule across the very top, mode-aware so
          // the light bar gets the deeper ember (border-box, so HEADER_HEIGHT
          // stays the true height).
          borderTop: `2px solid ${barEmber}`,
          borderBottom: `1px solid ${bar.rule}`,
          backgroundColor: scrolled ? alpha(bar.bg, isDark ? 0.8 : 0.85) : bar.bg,
          backdropFilter: scrolled ? "blur(12px) saturate(160%)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(12px) saturate(160%)" : "none",
          boxShadow: scrolled && !isDark ? "0 4px 12px rgba(11,11,12,0.06)" : "none",
          transition: theme.transitions.create(["background-color", "box-shadow", "border-color"], {
            duration: theme.transitions.duration.short,
          }),
        }}
      >
        <Container maxWidth="lg" sx={{ height: "100%" }}>
          <Toolbar
            disableGutters
            sx={{
              // Height flows from the AppBar's HEADER_HEIGHT via height:100%;
              // minHeight 0 keeps the toolbar inside the flag + bottom hairline.
              minHeight: 0,
              height: "100%",
              display: "flex",
              alignItems: "stretch",
              justifyContent: "space-between",
              gap: 3,
            }}
          >
            {/* Logo Area */}
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                flexShrink: 0,
                textDecoration: "none",
                transition: "opacity 150ms cubic-bezier(0.23, 1, 0.32, 1)",
                "&:hover": { opacity: 0.85 },
              }}
            >
              {/* Light masthead: the silver logo is drawn for a dark ground,
                  so it rides an obsidian tile (matching the footer). Dark: bare. */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  ...(isDark
                    ? {}
                    : { width: 30, height: 30, borderRadius: "7px", bgcolor: MASTHEAD_BG }),
                }}
              >
                <img
                  src={logo}
                  alt="Obsidian Ridge Logo"
                  width={isDark ? 26 : 20}
                  height={isDark ? 26 : 20}
                  style={{ height: isDark ? 26 : 20, width: isDark ? 26 : 20 }}
                />
              </Box>
              <Typography
                component="span"
                noWrap
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                  color: bar.fg,
                  display: { xs: "none", sm: "block" },
                  lineHeight: 1.2,
                }}
              >
                Obsidian Ridge
              </Typography>
              <Box
                component="span"
                sx={{
                  ...aiChipSx,
                  color: barEmber,
                  borderColor: alpha(barEmber, 0.4),
                  display: { xs: "none", sm: "inline-flex" },
                }}
              >
                AI
              </Box>
            </Box>

            {/* Desktop Nav - inline text links on the nameplate */}
            {!isMobile && (
              <Box
                component="nav"
                aria-label="Main navigation"
                sx={{ display: "flex", alignItems: "stretch", flexGrow: 1 }}
              >
                <Stack direction="row" spacing={1} sx={{ alignItems: "stretch" }}>
                  {mainNavItems.map((item) => {
                    const active = isActive(item.href);
                    return (
                      <Box
                        key={item.label}
                        component={RouterLink}
                        to={item.href}
                        aria-current={active ? "page" : undefined}
                        onMouseEnter={() => preloadMap[item.href]?.()}
                        onFocus={() => preloadMap[item.href]?.()}
                        sx={{
                          position: "relative",
                          display: "inline-flex",
                          alignItems: "center",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                          textDecoration: "none",
                          color: active ? bar.fg : bar.muted,
                          transition: "color 150ms cubic-bezier(0.23, 1, 0.32, 1)",
                          "&:hover": { color: bar.fg },
                          "&:hover .nav-pill": { bgcolor: navHover },
                          // 2px ember underline sitting on the bar's bottom
                          // hairline; inset to match the label inside the pill.
                          "&::after": active
                            ? {
                                content: '""',
                                position: "absolute",
                                left: 10,
                                right: 10,
                                bottom: 0,
                                height: 2,
                                bgcolor: "secondary.main",
                              }
                            : undefined,
                        }}
                      >
                        <Box
                          component="span"
                          className="nav-pill"
                          sx={{
                            px: 1.25,
                            py: 0.75,
                            borderRadius: "6px",
                            transition: "background-color 150ms cubic-bezier(0.23, 1, 0.32, 1)",
                          }}
                        >
                          {item.label}
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}

            {/* Right Actions */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ flexShrink: 0, justifyContent: "flex-end" }}
            >
              {!isMobile && (
                <>
                  {/* The masthead's one accent element (Stripe/Vercel pattern):
                      a single ember-filled CTA so the bar reads intentional. */}
                  <Button
                    component={RouterLink}
                    to="/contact"
                    variant="contained"
                    color="secondary"
                    size="small"
                    disableElevation
                    sx={{
                      borderRadius: "6px",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    Contact Us
                  </Button>

                  <IconButton
                    onClick={colorMode.toggleColorMode}
                    size="small"
                    aria-label="Toggle dark mode"
                    sx={{ color: bar.muted, "&:hover": { color: bar.fg } }}
                  >
                    {theme.palette.mode === "dark" ? (
                      <Brightness7Icon fontSize="small" />
                    ) : (
                      <Brightness4Icon fontSize="small" />
                    )}
                  </IconButton>

                  {renderUserActions()}
                </>
              )}

              {/* Mobile Nav Toggle */}
              {isMobile && (
                <IconButton
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                  sx={{ color: bar.muted, "&:hover": { color: bar.fg } }}
                >
                  <MenuIcon />
                </IconButton>
              )}
            </Stack>
          </Toolbar>
        </Container>

        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: 280,
              bgcolor: MASTHEAD_BG,
              backgroundImage: "none",
              borderLeft: `1px solid ${MASTHEAD_RULE}`,
            },
          }}
        >
          {drawer}
        </Drawer>
      </AppBar>
    </>
  );
};
